import React from 'react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteUser } from '@/services/user';
import { errorHandler } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuthenticationDispatch } from '@/hooks/use-authentication';
import { useNavigate } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';
import i18next from '@/i18next';
const deleteAccountSchema = z.object({
    confirmation: z
        .string()
        .trim()
        .refine((value) => value.toUpperCase() === 'DELETE', {
        message: i18next.t('security.delete_dialog.confirmation_error', { ns: 'profile' }),
    }),
});
export const DeleteButton = () => {
    const { t } = useTranslation('profile');
    const queryClient = useQueryClient();
    const dispatch = useAuthenticationDispatch();
    const navigate = useNavigate();
    const form = useForm({
        resolver: zodResolver(deleteAccountSchema),
        mode: 'onChange', // ✅ REQUIRED
        reValidateMode: 'onChange',
        defaultValues: {
            confirmation: '',
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: deleteUser,
        onSuccess: (data) => {
            // Keep behavior consistent: clear cache + auth state + redirect
            queryClient.clear();
            dispatch({ type: 'SIGN_OUT' });
            navigate({ to: '/sign-in' });
            toast.success(data.message);
        },
        onError: errorHandler,
    });
    const handleDeleteAccount = React.useCallback((_values) => {
        mutate();
    }, [mutate]);
    return (<Dialog>
            <DialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isPending}>
                    <Trash2 className="mr-1 h-3 w-3"/>
                    {t('security.delete')}
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="text-destructive">{t('security.delete_dialog.title')}</DialogTitle>
                    <DialogDescription>{t('security.delete_dialog.desc')}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <p>
                        <Trans i18nKey="security.delete_dialog.confirmation_hint" ns="profile" components={{ 1: <span className="font-mono"/> }}/>
                    </p>
                    <TextField control={form.control} name="confirmation" label={t('security.delete_dialog.confirmation_label')} required autoFocus autoComplete="off"/>
                </Form>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button">{t('security.delete_dialog.close')}</Button>
                    </DialogClose>
                    <Button variant="destructive" disabled={isPending || !form.formState.isValid} onClick={form.handleSubmit(handleDeleteAccount)}>
                        {isPending ? (<React.Fragment>
                                <Loader2 className="mr-1 h-3 w-3 animate-spin"/>
                                {t('security.delete_dialog.deleting')}
                            </React.Fragment>) : (<React.Fragment>
                                <Trash2 className="mr-1 h-3 w-3"/>
                                {t('security.delete_dialog.submit')}
                            </React.Fragment>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
};
